import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BRAND_LOGO_SITUATION_LABELS,
  BRAND_LOGO_SITUATIONS,
  type BrandDnaUpdateBody,
  type BrandLogoRecord,
} from '@marketing-hub/shared';
import { readCsrfCookie, CSRF_HEADER, needsCsrf } from '@/api/csrf';
import { apiFetch } from '@/api/client';
import { llmErrorMessage } from '@/features/llm/llmErrorMessage';
import type { Account } from '@/api/types';
import { ProjectGate } from '@/features/projects/ProjectGate';
import { ProjectPickerEmpty } from '@/features/projects/ProjectPickerEmpty';
import { ProjectWorkspaceHeader } from '@/features/projects/ProjectWorkspaceHeader';
import { useProjectContext } from '@/project/ProjectContext';
import { Button } from '@/components/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/components/ui/Field';
import { ErrorState, LoadingState } from '@/components/StateMessage/StateMessage';
import styles from './BrandDnaPage.module.css';

type BrandDnaResponse = BrandDnaUpdateBody & {
  project_id?: string;
  is_draft_defaults?: boolean;
};

type PaletteEntry = { hex: string; role: string };

function paletteFromExtra(extra: unknown): PaletteEntry[] | null {
  if (!extra || typeof extra !== 'object') return null;
  const palette = (extra as { color_palette?: unknown }).color_palette;
  if (!Array.isArray(palette)) return null;
  return palette
    .filter((p): p is PaletteEntry => {
      return !!p && typeof p === 'object' && typeof (p as PaletteEntry).hex === 'string';
    })
    .slice(0, 12);
}

function hasWebsiteColors(extra: unknown): boolean {
  return (
    !!extra &&
    typeof extra === 'object' &&
    (extra as { color_extraction_ok?: boolean }).color_extraction_ok === true
  );
}

function linesToList(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

function listToLines(items: string[] | undefined): string {
  return (items ?? []).join('\n');
}

function applyBrandDnaToForm(
  d: BrandDnaResponse,
  setters: {
    setTagline: (v: string) => void;
    setMission: (v: string) => void;
    setTone: (v: string) => void;
    setWritingStyle: (v: string) => void;
    setVisualStyle: (v: string) => void;
    setPrimaryColor: (v: string) => void;
    setSecondaryColor: (v: string) => void;
    setAccentColor: (v: string) => void;
    setFontHeading: (v: string) => void;
    setFontBody: (v: string) => void;
    setAudienceSummary: (v: string) => void;
    setValueProps: (v: string) => void;
    setKeywords: (v: string) => void;
    setAvoidPhrases: (v: string) => void;
  },
) {
  setters.setTagline(d.tagline ?? '');
  setters.setMission(d.mission ?? '');
  setters.setTone(d.tone_of_voice ?? '');
  setters.setWritingStyle(d.writing_style ?? '');
  setters.setVisualStyle(d.visual_style ?? '');
  setters.setPrimaryColor(d.primary_color ?? '');
  setters.setSecondaryColor(d.secondary_color ?? '');
  setters.setAccentColor(d.accent_color ?? '');
  const fontH = d.font_heading ?? '';
  const fontB = d.font_body ?? '';
  setters.setFontHeading(/^var\s*\(/i.test(fontH) ? '' : fontH);
  setters.setFontBody(/^var\s*\(/i.test(fontB) ? '' : fontB);
  setters.setAudienceSummary(d.target_audience_summary ?? '');
  setters.setValueProps(listToLines(d.value_props));
  setters.setKeywords(listToLines(d.keywords));
  setters.setAvoidPhrases(listToLines(d.avoid_phrases));
}

export function BrandDnaPage() {
  const { selectedProjectId, selectedProject, setSelectedProjectId } = useProjectContext();
  const qc = useQueryClient();

  const [tagline, setTagline] = useState('');
  const [mission, setMission] = useState('');
  const [tone, setTone] = useState('');
  const [writingStyle, setWritingStyle] = useState('');
  const [visualStyle, setVisualStyle] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [secondaryColor, setSecondaryColor] = useState('');
  const [accentColor, setAccentColor] = useState('');
  const [fontHeading, setFontHeading] = useState('');
  const [fontBody, setFontBody] = useState('');
  const [audienceSummary, setAudienceSummary] = useState('');
  const [valueProps, setValueProps] = useState('');
  const [keywords, setKeywords] = useState('');
  const [avoidPhrases, setAvoidPhrases] = useState('');
  const [aiScanNote, setAiScanNote] = useState<string | null>(null);
  const [detectedPalette, setDetectedPalette] = useState<PaletteEntry[] | null>(null);
  const [brandExtra, setBrandExtra] = useState<Record<string, unknown>>({});

  const [logoScope, setLogoScope] = useState<'project' | 'account'>('project');
  const [logoAccountId, setLogoAccountId] = useState('');
  const [logoSituation, setLogoSituation] = useState<string>('default');
  const [logoLabel, setLogoLabel] = useState('');

  const dnaQ = useQuery({
    queryKey: ['brand-dna', selectedProjectId],
    enabled: Boolean(selectedProjectId),
    queryFn: () =>
      apiFetch<{ brand_dna: BrandDnaResponse }>(`/api/projects/${selectedProjectId}/brand-dna`),
  });

  const accountsQ = useQuery({
    queryKey: ['accounts', selectedProjectId],
    enabled: Boolean(selectedProjectId),
    queryFn: () =>
      apiFetch<{ accounts: Account[] }>(`/api/accounts?project_id=${selectedProjectId}`),
  });

  const logoQueryKey = useMemo(
    () => ['brand-logos', selectedProjectId, logoScope, logoAccountId] as const,
    [selectedProjectId, logoScope, logoAccountId],
  );

  const logosQ = useQuery({
    queryKey: logoQueryKey,
    enabled: Boolean(selectedProjectId) && (logoScope === 'project' || Boolean(logoAccountId)),
    queryFn: () => {
      const params = new URLSearchParams({ project_id: selectedProjectId! });
      if (logoScope === 'account' && logoAccountId) {
        params.set('scope', 'account');
        params.set('account_id', logoAccountId);
      } else {
        params.set('scope', 'project');
      }
      return apiFetch<{ logos: BrandLogoRecord[] }>(`/api/brand-logos?${params}`);
    },
  });

  useEffect(() => {
    const d = dnaQ.data?.brand_dna;
    if (!d) return;
    const extracted = hasWebsiteColors(d.extra);
    setTagline(d.tagline ?? '');
    setMission(d.mission ?? '');
    setTone(d.tone_of_voice ?? '');
    setWritingStyle(d.writing_style ?? '');
    setVisualStyle(d.visual_style ?? '');
    setPrimaryColor(
      d.primary_color ??
        (extracted ? '' : d.is_draft_defaults ? (selectedProject?.color ?? '') : ''),
    );
    setSecondaryColor(d.secondary_color ?? '');
    setAccentColor(d.accent_color ?? '');
    const fh = d.font_heading ?? '';
    const fb = d.font_body ?? '';
    setFontHeading(/^var\s*\(/i.test(fh) ? '' : fh);
    setFontBody(/^var\s*\(/i.test(fb) ? '' : fb);
    setAudienceSummary(d.target_audience_summary ?? '');
    setValueProps(listToLines(d.value_props));
    setKeywords(listToLines(d.keywords));
    setAvoidPhrases(listToLines(d.avoid_phrases));
    if (d.extra && typeof d.extra === 'object') {
      setBrandExtra(d.extra as Record<string, unknown>);
    }
    setDetectedPalette(paletteFromExtra(d.extra));
  }, [dnaQ.data?.brand_dna, selectedProject?.color]);

  const formSetters = {
    setTagline,
    setMission,
    setTone,
    setWritingStyle,
    setVisualStyle,
    setPrimaryColor,
    setSecondaryColor,
    setAccentColor,
    setFontHeading,
    setFontBody,
    setAudienceSummary,
    setValueProps,
    setKeywords,
    setAvoidPhrases,
  };

  const suggest = useMutation({
    mutationFn: () =>
      apiFetch<{ suggested: BrandDnaResponse }>(
        `/api/projects/${selectedProjectId}/brand-dna/suggest`,
        { method: 'POST', body: JSON.stringify({}) },
      ),
    onSuccess: (res) => {
      applyBrandDnaToForm(res.suggested, formSetters);
      setAiScanNote(null);
    },
  });

  const aiScan = useMutation({
    mutationFn: () =>
      apiFetch<{
        suggested: BrandDnaResponse;
        model?: string;
        colors_from_website?: boolean;
      }>(`/api/projects/${selectedProjectId}/brand-dna/ai-scan`, {
        method: 'POST',
        body: JSON.stringify({ fetch_website: true }),
      }),
    onSuccess: (res) => {
      applyBrandDnaToForm(res.suggested, formSetters);
      if (res.suggested.extra && typeof res.suggested.extra === 'object') {
        setBrandExtra(res.suggested.extra as Record<string, unknown>);
      }
      setDetectedPalette(paletteFromExtra(res.suggested.extra));
      const colorNote = res.colors_from_website
        ? ' Kleuren komen uit website-CSS (theme, variabelen, stylesheets).'
        : '';
      setAiScanNote(
        res.model
          ? `AI-scan voltooid (${res.model}).${colorNote} Controleer en klik Opslaan.`
          : `AI-scan voltooid.${colorNote} Controleer en klik Opslaan.`,
      );
    },
  });

  const save = useMutation({
    mutationFn: () =>
      apiFetch(`/api/projects/${selectedProjectId}/brand-dna`, {
        method: 'PUT',
        body: JSON.stringify({
          tagline: tagline.trim() || null,
          mission: mission.trim() || null,
          tone_of_voice: tone.trim() || null,
          writing_style: writingStyle.trim() || null,
          visual_style: visualStyle.trim() || null,
          primary_color: primaryColor.trim() || null,
          secondary_color: secondaryColor.trim() || null,
          accent_color: accentColor.trim() || null,
          font_heading: fontHeading.trim() || null,
          font_body: fontBody.trim() || null,
          target_audience_summary: audienceSummary.trim() || null,
          value_props: linesToList(valueProps),
          keywords: linesToList(keywords),
          avoid_phrases: linesToList(avoidPhrases),
          extra: {
            ...brandExtra,
            color_palette: detectedPalette ?? brandExtra.color_palette,
            colors_saved_at: new Date().toISOString(),
          },
        } satisfies BrandDnaUpdateBody),
      }),
    onSuccess: () => {
      setAiScanNote('Opgeslagen.');
      void qc.invalidateQueries({ queryKey: ['brand-dna', selectedProjectId] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });

  const deleteLogo = useMutation({
    mutationFn: (id: string) => apiFetch(`/api/brand-logos/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: logoQueryKey }),
  });

  async function uploadLogo(file: File) {
    if (!selectedProjectId) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('project_id', selectedProjectId);
    fd.append('situation', logoSituation);
    if (logoLabel.trim()) fd.append('label', logoLabel.trim());
    if (logoScope === 'account' && logoAccountId) fd.append('account_id', logoAccountId);
    const headers = new Headers();
    if (needsCsrf('POST')) {
      const token = readCsrfCookie();
      if (token) headers.set(CSRF_HEADER, token);
    }
    const res = await fetch('/api/brand-logos', {
      method: 'POST',
      body: fd,
      credentials: 'include',
      headers,
    });
    if (!res.ok) {
      const err = (await res.json().catch(() => null)) as { error?: string } | null;
      throw new Error(err?.error ?? 'Upload mislukt');
    }
    void qc.invalidateQueries({ queryKey: logoQueryKey });
    setLogoLabel('');
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  const isDraft = dnaQ.data?.brand_dna?.is_draft_defaults;

  return (
    <ProjectGate pageTitle="Bedrijfs-DNA">
      {!selectedProjectId ? (
        <ProjectPickerEmpty onSelect={setSelectedProjectId} />
      ) : dnaQ.isLoading ? (
        <LoadingState />
      ) : dnaQ.isError ? (
        <ErrorState message={(dnaQ.error as Error).message} />
      ) : (
        <div className={styles.page}>
          <ProjectWorkspaceHeader
            title="Bedrijfs-DNA"
            subtitle="Kleuren, tone of voice, doelgroepen en logo's — centraal per project (en per account waar nodig)."
            projectName={selectedProject?.name ?? 'Project'}
            onSwitchProject={() => setSelectedProjectId(null)}
          />

          <div className={styles.toolbar}>
            {isDraft ? (
              <p className={styles.muted}>
                Nog niet opgeslagen — velden zijn vooringevuld vanuit project, kleur en doelgroepen.
              </p>
            ) : (
              <p className={styles.muted}>Laatst opgeslagen profiel voor dit project.</p>
            )}
            <div className={styles.toolbarActions}>
              <Button
                type="button"
                disabled={aiScan.isPending || suggest.isPending}
                onClick={() => aiScan.mutate()}
              >
                {aiScan.isPending ? 'AI scan…' : 'AI scan (vullen)'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={suggest.isPending || aiScan.isPending}
                onClick={() => suggest.mutate()}
              >
                Regels vullen (zonder AI)
              </Button>
            </div>
          </div>
          {aiScan.isError ? (
            <p className={styles.muted} role="alert">
              {llmErrorMessage(aiScan.error)}
            </p>
          ) : null}
          {aiScanNote ? <p className={styles.muted}>{aiScanNote}</p> : null}

          <form onSubmit={onSubmit}>
            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Identiteit</h2>
              <div className={styles.grid}>
                <Field label="Tagline">
                  <TextInput value={tagline} onChange={(e) => setTagline(e.target.value)} />
                </Field>
                <Field label="Missie / positionering" compact>
                  <TextArea value={mission} onChange={(e) => setMission(e.target.value)} rows={3} />
                </Field>
              </div>
            </section>

            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Kleuren &amp; typografie</h2>
              <div className={styles.grid}>
                <Field label="Primair">
                  <div className={styles.colorRow}>
                    <span className={styles.colorSwatch} style={{ background: primaryColor }} />
                    <TextInput value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} />
                  </div>
                </Field>
                <Field label="Secundair">
                  <div className={styles.colorRow}>
                    <span
                      className={styles.colorSwatch}
                      style={{ background: secondaryColor || '#eee' }}
                    />
                    <TextInput
                      value={secondaryColor}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                    />
                  </div>
                </Field>
                <Field label="Accent">
                  <div className={styles.colorRow}>
                    <span
                      className={styles.colorSwatch}
                      style={{ background: accentColor || '#eee' }}
                    />
                    <TextInput value={accentColor} onChange={(e) => setAccentColor(e.target.value)} />
                  </div>
                </Field>
                <Field label="Koptekst-font">
                  <TextInput value={fontHeading} onChange={(e) => setFontHeading(e.target.value)} />
                </Field>
                <Field label="Body-font">
                  <TextInput value={fontBody} onChange={(e) => setFontBody(e.target.value)} />
                </Field>
              </div>
              {detectedPalette?.length ? (
                <div className={styles.paletteRow}>
                  <span className={styles.muted}>Gedetecteerd palet (website):</span>
                  <ul className={styles.paletteList}>
                    {detectedPalette.map((p) => (
                      <li key={`${p.role}-${p.hex}`} title={p.role}>
                        <span className={styles.colorSwatch} style={{ background: p.hex }} />
                        <span className={styles.paletteMeta}>
                          {p.hex} · {p.role}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <Field label="Visuele stijl">
                <TextArea
                  value={visualStyle}
                  onChange={(e) => setVisualStyle(e.target.value)}
                  rows={2}
                  placeholder="bijv. clean, veel witruimte, foto's met mensen"
                />
              </Field>
            </section>

            <section className={styles.card}>
              <h2 className={styles.cardTitle}>Tone of voice &amp; doelgroep</h2>
              <Field label="Tone of voice">
                <TextArea
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  rows={3}
                  placeholder="bijv. direct, nuchter, u op informeel MKB"
                />
              </Field>
              <Field label="Schrijfstijl">
                <TextArea value={writingStyle} onChange={(e) => setWritingStyle(e.target.value)} rows={2} />
              </Field>
              <Field label="Doelgroepen (samenvatting)" hint="Geaggregeerd uit doelgroepen in dit project">
                <TextArea
                  value={audienceSummary}
                  onChange={(e) => setAudienceSummary(e.target.value)}
                  rows={5}
                />
              </Field>
              <div className={styles.grid}>
                <Field label="Kernboodschappen (één per regel)">
                  <TextArea value={valueProps} onChange={(e) => setValueProps(e.target.value)} rows={4} />
                </Field>
                <Field label="Keywords (één per regel)">
                  <TextArea value={keywords} onChange={(e) => setKeywords(e.target.value)} rows={4} />
                </Field>
                <Field label="Niet gebruiken (één per regel)">
                  <TextArea value={avoidPhrases} onChange={(e) => setAvoidPhrases(e.target.value)} rows={4} />
                </Field>
              </div>
            </section>

            <div className={styles.toolbar}>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Opslaan…' : 'Bedrijfs-DNA opslaan'}
              </Button>
              {save.isError ? (
                <p className={styles.muted}>{(save.error as Error).message}</p>
              ) : null}
            </div>
          </form>

          <section className={styles.card}>
            <h2 className={styles.cardTitle}>Logo&apos;s</h2>
            <p className={styles.muted}>
              Meerdere varianten per situatie (e-mail, social, donkere achtergrond, co-branding, …).
            </p>
            <div className={styles.uploadRow}>
              <Field label="Eigenaar">
                <SelectInput
                  value={logoScope}
                  onChange={(e) => setLogoScope(e.target.value as 'project' | 'account')}
                >
                  <option value="project">Dit project (eigen merk)</option>
                  <option value="account">Account / bedrijf</option>
                </SelectInput>
              </Field>
              {logoScope === 'account' ? (
                <Field label="Account">
                  <SelectInput value={logoAccountId} onChange={(e) => setLogoAccountId(e.target.value)}>
                    <option value="">— kies —</option>
                    {(accountsQ.data?.accounts ?? []).map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </SelectInput>
                </Field>
              ) : null}
              <Field label="Situatie">
                <SelectInput value={logoSituation} onChange={(e) => setLogoSituation(e.target.value)}>
                  {BRAND_LOGO_SITUATIONS.map((s) => (
                    <option key={s} value={s}>
                      {BRAND_LOGO_SITUATION_LABELS[s]}
                    </option>
                  ))}
                </SelectInput>
              </Field>
              <Field label="Label (optioneel)">
                <TextInput value={logoLabel} onChange={(e) => setLogoLabel(e.target.value)} />
              </Field>
              <Field label="Bestand">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  disabled={logoScope === 'account' && !logoAccountId}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) void uploadLogo(f).catch(() => undefined);
                    e.target.value = '';
                  }}
                />
              </Field>
            </div>

            {logosQ.isLoading ? (
              <LoadingState label="Logo's laden…" />
            ) : (
              <div className={styles.logoGrid}>
                {(logosQ.data?.logos ?? []).map((logo) => (
                  <div key={logo.id} className={styles.logoTile}>
                    <img
                      className={styles.logoImg}
                      src={logo.url}
                      alt={logo.label ?? logo.original_file_name}
                    />
                    <div className={styles.logoMeta}>
                      {BRAND_LOGO_SITUATION_LABELS[logo.situation]}
                      {logo.label ? ` · ${logo.label}` : ''}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={deleteLogo.isPending}
                      onClick={() => deleteLogo.mutate(logo.id)}
                    >
                      Verwijderen
                    </Button>
                  </div>
                ))}
                {!logosQ.data?.logos.length ? (
                  <p className={styles.muted}>Nog geen logo&apos;s voor deze selectie.</p>
                ) : null}
              </div>
            )}
          </section>
        </div>
      )}
    </ProjectGate>
  );
}
